/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CalendarClock, ClipboardCheck } from 'lucide-react';
import { useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type PlanReview } from '../../lib/api/reviews.js';
import { useReviews } from '../../lib/queries/reviews.js';

import { RecordReviewModal } from './record-review-modal.js';

/** Today + 3 months as `YYYY-MM-DD` — the default next review date (BRD M01). */
function defaultNextReviewDate(): string {
  const date = new Date();
  date.setMonth(date.getMonth() + 3);
  return date.toISOString().slice(0, 10);
}

/** Whether a review date is on or before today (review fallen due). */
function isDue(reviewDate: string | null): boolean {
  return reviewDate !== null && reviewDate <= new Date().toISOString().slice(0, 10);
}

function ReviewItem({ review }: { readonly review: PlanReview }): JSX.Element {
  const { t } = useTranslation();
  return (
    <li className="rounded-xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700">
          {t(`therapist.review.outcome_${review.outcome}`)}
        </span>
        <span className="text-xs text-slate-400">
          {review.reviewedAt.slice(0, 10)}
          {review.reviewedByName !== null && ` · ${review.reviewedByName}`}
        </span>
      </div>
      <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{review.summary}</p>
      {review.recommendations !== null && (
        <p className="mt-1.5 text-sm text-slate-500">{review.recommendations}</p>
      )}
      {review.nextReviewDate !== null && (
        <p className="mt-2 text-xs text-slate-400">
          {t('therapist.review.nextDate')}: {review.nextReviewDate}
        </p>
      )}
    </li>
  );
}

interface ReviewsSectionProps {
  readonly planId: string;
  readonly reviewDate: string | null;
}

/** Plan reviews (BRD M01) — due badge, history, and the record-review action. */
export function ReviewsSection({ planId, reviewDate }: ReviewsSectionProps): JSX.Element {
  const { t } = useTranslation();
  const reviews = useReviews(planId);
  const [open, setOpen] = useState(false);
  const list = useMemo(() => reviews.data ?? [], [reviews.data]);
  const due = isDue(reviewDate);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-700">{t('therapist.review.title')}</p>
        <div className="flex items-center gap-2">
          {due && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
              <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
              {t('therapist.review.due')}
            </span>
          )}
          <button
            type="button"
            onClick={openModal}
            className="focus-ring inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
            {t('therapist.review.record')}
          </button>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400">{t('therapist.review.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {list.map((review) => (
            <ReviewItem key={review.id} review={review} />
          ))}
        </ul>
      )}
      <RecordReviewModal
        planId={planId}
        open={open}
        onOpenChange={setOpen}
        defaultNextDate={defaultNextReviewDate()}
        onSaved={closeModal}
      />
    </div>
  );
}
